import { Card, Col, Row, Typography, Space, Input, Button, Statistic, Select } from "antd";
import { BankOutlined, EditOutlined, SaveOutlined, WalletOutlined, SettingOutlined } from "@ant-design/icons";
import { useState, useEffect } from "react";
import useRequest from "../hooks/useRequest";
import { ShowToast, Severty } from "../helper/toast";

const { Text, Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const DEFAULT_FORM_DATA = {
    account_name: "",
    account_number: 0,
    merchant_id: null,
    admin_wallet: null,
    max_bank_accounts_per_user: 1,
};

const TrustAccountManager = () => {
    const [isEditing, setIsEditing] = useState(false);
    const { request } = useRequest();
    const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
    const [originalData, setOriginalData] = useState(DEFAULT_FORM_DATA);

    // Merchants state
    const [merchants, setMerchants] = useState([]);

    const fetchData = () => {
        request({
            url: "/api/admin/trust-account",
            method: "GET",
            onSuccess: (res) => {
                const data = res?.data || DEFAULT_FORM_DATA;
                const nextData = {
                    ...DEFAULT_FORM_DATA,
                    ...data,
                    merchant_id: data.admin_wallet?.merchant_id || null,
                    max_bank_accounts_per_user: data.max_bank_accounts_per_user || 1,
                };
                setFormData(nextData);
                setOriginalData(nextData);
            },
            onError: () => {
                ShowToast("Failed to fetch trust accounts", Severty.ERROR);
            },
        });
    };

    const fetchMerchants = () => {
        request({
            url: "/api/admin/merchant-profile",
            method: "GET",
            onSuccess: (res) => {
                const list = res?.data?.results || res?.data || [];
                setMerchants(list);
            },
        });
    };

    useEffect(() => {
        fetchData();
        fetchMerchants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // handle change
    const handleChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    // edit click
    const handleEdit = () => {
        setOriginalData(formData); // store old data
        setIsEditing(true);
    };

    // cancel click
    const handleCancel = () => {
        setFormData(originalData); // revert back
        setIsEditing(false);
    };

    const handleSave = () => {
        request({
            url: "/api/admin/trust-account",
            method: "POST",
            data: {
                account_name: formData.account_name,
                account_number: formData.account_number,
                max_bank_accounts_per_user: formData.max_bank_accounts_per_user,
                merchant_id: formData.merchant_id,
            },
            onSuccess: () => {
                ShowToast("Trust account saved successfully", Severty.SUCCESS);
                fetchData();
                setIsEditing(false);
            },
            onError: () => {
                ShowToast("Failed to save trust account", Severty.ERROR);
            },
        });
    };

    return (
        <Card>
            <Row justify="space-between" align="middle" style={{ marginBottom: 32 }}>
                <div>
                    <Title level={2} style={{ margin: 0 }}>
                        System & Trust Configuration
                    </Title>
                    <Text type="secondary">
                        Manage system bank accounts and the designated primary Admin Wallet
                    </Text>
                </div>
                {!isEditing ? (
                    <Button type="primary" icon={<EditOutlined />} onClick={handleEdit}>
                        Edit Configuration
                    </Button>
                ) : (
                    <Space>
                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            onClick={handleSave}
                        >
                            Save Changes
                        </Button>
                        <Button onClick={handleCancel}>Cancel</Button>
                    </Space>
                )}
            </Row>

            <Row gutter={[16, 16]}>
                {/* TRUST ACCOUNT INFO */}
                <Col xs={24} md={12}>
                    <Card hoverable style={{ borderRadius: 16, width: "100%", height: "100%" }}>
                        <Space direction="vertical" size={16} style={{ width: "100%" }}>
                            <BankOutlined style={{ fontSize: 26, color: "#1677ff" }} />
                            <Title level={4} style={{ margin: 0 }}>Trust Bank Account</Title>

                            <div>
                                <Text type="secondary">Account Name</Text>
                                <Input
                                    value={formData.account_name}
                                    disabled={!isEditing}
                                    onChange={(e) =>
                                        handleChange("account_name", e.target.value)
                                    }
                                />
                            </div>

                            <div>
                                <Text type="secondary">Account Number</Text>
                                <TextArea
                                    value={formData.account_number}
                                    disabled={!isEditing}
                                    autoSize={{ minRows: 1, maxRows: 2 }}
                                    onChange={(e) =>
                                        handleChange("account_number", e.target.value)
                                    }
                                />
                            </div>
                        </Space>
                    </Card>
                </Col>

                {/* SINGLE PRIMARY ADMIN WALLET */}
                <Col xs={24} md={12}>
                    <Card hoverable style={{ borderRadius: 16, width: "100%", height: "100%" }}>
                        <Space direction="vertical" size={16} style={{ width: "100%" }}>
                            <WalletOutlined style={{ fontSize: 26, color: "#52c41a" }} />
                            <Title level={4} style={{ margin: 0 }}>System Admin Wallet</Title>
                            
                            <Statistic 
                                title="Current Balance" 
                                value={formData?.admin_wallet?.balance || 0} 
                                prefix="$" 
                                precision={2} 
                                valueStyle={{ color: '#52c41a' }}
                            />
                            
                            <div>
                                <Text type="secondary" style={{ display: 'block' }}>Map to Merchant Profile</Text>
                                <Select
                                    showSearch
                                    style={{ width: '100%' }}
                                    placeholder="Select a Merchant for Admin Wallet"
                                    optionFilterProp="children"
                                    value={formData.merchant_id}
                                    onChange={(val) => handleChange("merchant_id", val)}
                                    disabled={!isEditing}
                                >
                                    {merchants.map(m => (
                                        <Option key={m.id} value={m.id}>
                                            {m.business_name} ({m.merchant_code})
                                        </Option>
                                    ))}
                                </Select>
                            </div>
                            
                            <div>
                                <Text type="secondary">System Wallet ID: </Text>
                                <Text strong>{formData?.admin_wallet?.wallet_id || "N/A"}</Text>
                            </div>
                            
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                This designated wallet acts as the counterparty for double-entry bookkeeping on all platform transactions, fee accruals, and subscription packages.
                            </Text>
                        </Space>
                    </Card>
                </Col>

                {/* USER CONSTRAINTS */}
                <Col xs={24} md={12}>
                    <Card hoverable style={{ borderRadius: 16, width: "100%", height: "100%" }}>
                        <Space direction="vertical" size={16} style={{ width: "100%" }}>
                            <SettingOutlined style={{ fontSize: 26, color: "#faad14" }} />
                            <Title level={4} style={{ margin: 0 }}>User Constraints</Title>
                            
                            <div>
                                <Text type="secondary" style={{ display: 'block' }}>Max Bank Accounts per User</Text>
                                <Input
                                    type="number"
                                    min={0}
                                    style={{ width: "100%" }}
                                    value={formData.max_bank_accounts_per_user}
                                    disabled={!isEditing}
                                    onChange={(e) =>
                                        handleChange("max_bank_accounts_per_user", e.target.value)
                                    }
                                />
                            </div>
                            
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                This limits the total number of connected bank accounts a single user can have active on their profile at one time. Set this value to 0 to allow unlimited active bank accounts.
                            </Text>
                        </Space>
                    </Card>
                </Col>
            </Row>

        </Card>
    );
};

export default TrustAccountManager;